import clsx from 'clsx';
import { useMemo, Fragment, useRef, useEffect, type ReactElement } from 'react';

import { getOrCreateNamedHighlight } from './CSSHighlight.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import * as filterfieldStyles from '../FilterField.sty.js';
import * as styles from './FilterStatement.sty.js';
import {
  FilterFieldStringNode,
  type FilterFieldBigIntNode,
  type FilterFieldBooleanNode,
  type FilterFieldComparisonOperatorNode,
  type FilterFieldDurationNode,
  type FilterFieldExistsOperatorNode,
  type FilterFieldGroupNode,
  type FilterFieldInclusionOperatorNode,
  type FilterFieldKeyNode,
  type FilterFieldLeafNode,
  type FilterFieldListNode,
  type FilterFieldNode,
  type FilterFieldNodeBase,
  type FilterFieldNumberNode,
  type FilterFieldRootNode,
  type FilterFieldSearchOperatorNode,
  type FilterFieldStatementNode,
  type FilterFieldValueNode,
  type FilterFieldVariableNode,
  type FilterFieldJSONPathNode,
  FilterFieldErrorNode,
} from '../types/tree-nodes.js';
import { convertStringToFilterFieldTree } from '../utils/conversion/string-tree-conversion.js';
import {
  isFilterFieldBigIntNode,
  isFilterFieldBooleanNode,
  isFilterFieldComparisonOperatorNode,
  isFilterFieldDurationNode,
  isFilterFieldErrorNode,
  isFilterFieldExistsOperatorNode,
  isFilterFieldGroupNode,
  isFilterFieldInclusionOperatorNode,
  isFilterFieldJSONPathNode,
  isFilterFieldKeyNode,
  isFilterFieldLeafNode,
  isFilterFieldListNode,
  isFilterFieldNumberNode,
  isFilterFieldSearchOperatorNode,
  isFilterFieldStatementNode,
  isFilterFieldStringNode,
  isFilterFieldVariableNode,
} from '../utils/tree-node-guards.js';

function WhitespaceRenderer(
  props: Readonly<{
    start?: FilterFieldNodeBase;
    end?: FilterFieldNodeBase;
  }>,
) {
  const { start: startNode, end: endNode } = props;

  if (!startNode || !endNode) {
    return null;
  }

  const numberOfSpaces = Math.max(
    0,
    (endNode.range.from ?? -1) - (startNode.range.to ?? -1),
  );
  return Array(numberOfSpaces)
    .fill(' ')
    .map((item, index) => (
      <span className={styles.whitespace} key={`${item}-${index}`}>
        &#32;
      </span>
    ));
}

function withNodeRenderer<T extends FilterFieldLeafNode | FilterFieldListNode>(
  className: string,
) {
  return function Component(props: Readonly<{ node: T }>) {
    const { node } = props;

    const ref = useRef<HTMLSpanElement>(null);
    const highlight = useMemo(
      () => getOrCreateNamedHighlight('filter-field-escape-characters'),
      [],
    );
    useEffect(() => {
      const ranges: Array<Range> = [];
      if (node?.textValue.includes('\\') && ref.current) {
        const textNode = ref.current.childNodes.item(0);
        if (textNode) {
          const textContent = textNode.textContent ?? '';
          let index = 0;
          while ((index = textContent.indexOf('\\', index)) !== -1) {
            // ignore if there is nothing to escape.
            if (index + 1 >= textContent.length) {
              break;
            }
            const range = new Range();
            range.setStart(textNode, index);
            range.setEnd(textNode, index + 2);
            ranges.push(range);
            index += 2; // Move past the current match
            highlight.add(range);
          }
        }
      }

      return () => {
        for (const range of ranges) {
          highlight.delete(range);
        }
      };
    }, [highlight, node?.textValue]);

    return (
      <span ref={ref} className={clsx(styles.escapeCharacter, className)}>
        {node?.textValue}
      </span>
    );
  };
}
const KeyRenderer = withNodeRenderer<FilterFieldKeyNode>(
  filterfieldStyles.filterKey,
);
const StringRenderer = withNodeRenderer<FilterFieldStringNode>(
  filterfieldStyles.filterValue,
);
const ComparisonOperatorRenderer =
  withNodeRenderer<FilterFieldComparisonOperatorNode>(
    filterfieldStyles.operator,
  );
const ListRenderer = withNodeRenderer<FilterFieldListNode>('');
const VariableRenderer = withNodeRenderer<FilterFieldVariableNode>(
  filterfieldStyles.variable,
);

const JSONPathRenderer = withNodeRenderer<FilterFieldJSONPathNode>(
  filterfieldStyles.filterValue,
);
const NumberRenderer = withNodeRenderer<FilterFieldNumberNode>(
  filterfieldStyles.filterValue,
);
const BigIntRenderer = withNodeRenderer<FilterFieldBigIntNode>(
  filterfieldStyles.filterValue,
);
const BooleanRenderer = withNodeRenderer<FilterFieldBooleanNode>(
  filterfieldStyles.filterValue,
);
const DurationRenderer = withNodeRenderer<FilterFieldDurationNode>(
  filterfieldStyles.filterValue,
);
const ExistsOperatorRenderer = withNodeRenderer<FilterFieldExistsOperatorNode>(
  filterfieldStyles.operator,
);
const SearchOperatorRenderer = withNodeRenderer<FilterFieldSearchOperatorNode>(
  filterfieldStyles.operator,
);
const InclusionOperatorRenderer =
  withNodeRenderer<FilterFieldInclusionOperatorNode>(
    filterfieldStyles.operator,
  );
const ErrorRenderer = withNodeRenderer<FilterFieldErrorNode>(
  filterfieldStyles.error,
);

function GroupRenderer(props: Readonly<{ node: FilterFieldGroupNode }>) {
  const { node } = props;
  const lastChild = node.children[node.children.length - 1];
  return (
    <span>
      {node.explicit ? '(' : ''}
      {node.children.map((childNode, index, allChildren) => {
        const isLast = index === allChildren.length - 1;
        const nextChild = node.children[index + 1];
        let joiner: ReactElement | null = null;

        if (
          node.explicitLogicalOperator &&
          !isLast &&
          nextChild?.type !== 'Error' &&
          (!('value' in nextChild) || nextChild.value !== '')
        ) {
          joiner = (
            <span className={filterfieldStyles.operator}>
              <span className={styles.whitespace}>&#32;</span>
              {node.logicalOperator}
              <span className={styles.whitespace}>&#32;</span>
            </span>
          );
        } else if (node.explicitLogicalOperator === false && !isLast) {
          joiner = (
            <WhitespaceRenderer
              start={childNode}
              end={allChildren.at(index + 1)}
            />
          );
        }

        return (
          <Fragment key={`${childNode.range.from}-${childNode.range.to}`}>
            <NodeRenderer node={childNode} />
            {joiner}
          </Fragment>
        );
      })}
      {node.explicit &&
      lastChild.type !== 'Error' &&
      (!('value' in lastChild) || lastChild.value !== '')
        ? ')'
        : ''}
    </span>
  );
}

function ValueRenderer(props: Readonly<{ node?: FilterFieldValueNode }>) {
  const { node } = props;

  if (isFilterFieldLeafNode(node)) {
    return <LeafNodeRenderer node={node} />;
  }
  if (isFilterFieldListNode(node)) {
    return <ListRenderer node={node} />;
  }
  return null;
}

function StatementRenderer(
  props: Readonly<{ node: FilterFieldStatementNode }>,
) {
  const { node } = props;
  return (
    <span className={styles.statement}>
      <KeyRenderer node={node.key as FilterFieldKeyNode} />
      <WhitespaceRenderer start={node.key} end={node.comparisonOperator} />
      <LeafNodeRenderer node={node.comparisonOperator} />
      <WhitespaceRenderer start={node.comparisonOperator} end={node.value} />
      <ValueRenderer node={node.value} />
    </span>
  );
}

function LeafNodeRenderer(props: Readonly<{ node?: FilterFieldLeafNode }>) {
  const { node } = props;

  if (isFilterFieldKeyNode(node)) {
    return <KeyRenderer node={node} />;
  }
  if (isFilterFieldComparisonOperatorNode(node)) {
    return <ComparisonOperatorRenderer node={node} />;
  }
  if (isFilterFieldStringNode(node)) {
    return <StringRenderer node={node} />;
  }
  if (isFilterFieldVariableNode(node)) {
    return <VariableRenderer node={node} />;
  }
  if (isFilterFieldNumberNode(node)) {
    return <NumberRenderer node={node} />;
  }
  if (isFilterFieldJSONPathNode(node)) {
    return <JSONPathRenderer node={node} />;
  }
  if (isFilterFieldBigIntNode(node)) {
    return <BigIntRenderer node={node} />;
  }
  if (isFilterFieldBooleanNode(node)) {
    return <BooleanRenderer node={node} />;
  }
  if (isFilterFieldDurationNode(node)) {
    return <DurationRenderer node={node} />;
  }
  if (isFilterFieldExistsOperatorNode(node)) {
    return <ExistsOperatorRenderer node={node} />;
  }
  if (isFilterFieldSearchOperatorNode(node)) {
    return <SearchOperatorRenderer node={node} />;
  }
  if (isFilterFieldInclusionOperatorNode(node)) {
    return <InclusionOperatorRenderer node={node} />;
  }
  if (isFilterFieldErrorNode(node)) {
    return <ErrorRenderer node={node} />;
  }

  return null;
}

export function NodeRenderer(
  props: Readonly<{ node: FilterFieldRootNode | FilterFieldNode }>,
) {
  const { node } = props;

  if (isFilterFieldGroupNode(node)) {
    return <GroupRenderer node={node} />;
  }

  if (isFilterFieldStatementNode(node)) {
    return <StatementRenderer node={node} />;
  }

  if (isFilterFieldErrorNode(node)) {
    return <ErrorRenderer node={node} />;
  }

  return null;
}

/**
 * Accepted properties for FilterStatement.
 * @internal
 */
export type FilterStatementProps = StylingProps &
  DataTestId & { value?: string; highlighting?: 'syntax' | 'minimal' };

/**
 * The `FilterStatement` component renders and highlights a filter string
 * using an optimized parser for syntax highlighting.
 * @internal
 */
export function FilterStatement(props: FilterStatementProps) {
  const {
    value: filterString,
    'data-testid': dataTestId,
    className: consumerClassName,
    style: consumerStyle,
    highlighting = 'syntax',
  } = props;

  const tokenizedValue = useMemo(() => {
    return convertStringToFilterFieldTree(filterString ?? '', {
      parserConfig: {
        jsonPathConversion: true,
        searchConversion: true,
      },
    });
  }, [filterString]);

  return (
    <div
      className={clsx(styles.root, consumerClassName, [
        highlighting === 'syntax' && styles.highlighting.syntax,
        highlighting === 'minimal' && styles.highlighting.minimal,
      ])}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      <NodeRenderer node={tokenizedValue.tree as FilterFieldRootNode} />
    </div>
  );
}
